import { useId, useRef, useState } from 'react';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { useTranslation } from 'next-i18next';
import type { AvatarData } from '@/api/gen/_model/avatarData';
import { useViewport } from '@/hooks';
import { ChevronRightIcon } from '@/icons';
import { useCreateClassroom, useUpdateClassroom } from '@/pages/classrooms/shared/hooks';
import { getClassroomGradientBackground } from '@/pages/classrooms/shared/utils';
import { CLASSROOM_ROUTES } from '@/shared/constants/routes';
import { CLASSROOM_NAME_MAX_LENGTH } from '@/shared/utils/classroomValidation';
import { Button, confirmDialog, DefaultDialog } from '@/ui_v3';
import { cn } from '@/utils/tailwind.util';
import { type ClassroomItem, OwnerClassroomCard } from './ClassroomCards';
export type CreatedClassroom = {
  classroomId: string;
  name: string;
  ownerName: string;
  ownerAvatarData?: AvatarData;
  memberCount: number;
};
export function CreatedClassroomModal({
  onClose,
  classroomId,
  name,
  ownerName,
  ownerAvatarData,
  memberCount,
  onGoToClass
}: CreatedClassroom & {
  onClose: () => void;
  onGoToClass: (id: string) => void;
}) {
  const {
    t
  } = useTranslation(['classroom']);
  const {
    isMobile
  } = useViewport();
  const gradientBackground = getClassroomGradientBackground(classroomId || name);
  const createdClassroomItem: ClassroomItem = {
    id: classroomId,
    name,
    subtitle: ownerName,
    ownerAvatarData,
    memberCount,
    isOwner: true,
    href: CLASSROOM_ROUTES.detail(classroomId, true, {
      isMobile
    })
  };
  return <DefaultDialog onClose={onClose} size="md" className="w-[520px] max-w-[calc(100vw-40px)]" header={{
    title: t('classroom:createClassroomModal.createdTitle')
  }} body={<div className="flex h-[324px] items-center justify-center px-lg py-[80px]" style={{
    background: gradientBackground
  }}>
          <OwnerClassroomCard item={createdClassroomItem} onClick={() => undefined} className="max-w-[360px] cursor-default hover:border-alternative hover:shadow-xs" />
        </div>} footer={<Button variant="contained" intent="strong" size="lg" className="w-full" rightSlot={<ChevronRightIcon size="sm" fill={ICON_COLOR.inverse} />} onClick={() => onGoToClass(classroomId)}>
          {t('classroom:createClassroomModal.enterButton')}
        </Button>} data-sentry-element="DefaultDialog" data-sentry-component="CreatedClassroomModal" data-sentry-source-file="ClassroomCreateModals.tsx" />;
}
function ClassroomNameFormDialog({
  title,
  labelText,
  initialName = '',
  submitLabel,
  placeholder,
  onSubmit,
  onClose,
  isPending = false,
  errorMessage,
  onErrorMessageClear
}: {
  title: string;
  labelText: string;
  initialName?: string;
  submitLabel: string;
  placeholder?: string;
  onSubmit: (name: string) => void;
  onClose: () => void;
  isPending?: boolean;
  errorMessage?: string;
  onErrorMessageClear?: () => void;
}) {
  const inputId = useId();
  const [name, setName] = useState(initialName);
  const trimmed = name.trim();
  const isValid = trimmed.length > 0 && trimmed.length <= CLASSROOM_NAME_MAX_LENGTH;
  const handleSubmit = () => {
    if (!isValid || isPending) return;
    onSubmit(trimmed);
  };
  return <DefaultDialog onClose={onClose} size="md" className="w-[520px] max-w-[calc(100vw-40px)]" header={{
    title,
    hasCloseButton: true
  }} body={<div className="px-md mobile-lg:px-lg">
          <div className="flex flex-col gap-3xs">
            <div className="flex items-end justify-between">
              <label htmlFor={inputId} className="text-body-lg-bold text-default">
                {labelText}
              </label>
              <span className="pb-4xs text-body-sm-medium text-assistive">
                {name.length}/{CLASSROOM_NAME_MAX_LENGTH}
              </span>
            </div>
            <div className={cn('flex min-h-[48px] items-center rounded-sm border border-alternative bg-default px-sm', errorMessage && 'border-[#FFACB6]')}>
              <input id={inputId} type="text" value={name} autoFocus maxLength={CLASSROOM_NAME_MAX_LENGTH} placeholder={placeholder} onChange={e => {
          setName(e.target.value.slice(0, CLASSROOM_NAME_MAX_LENGTH));
          onErrorMessageClear?.();
        }} className={cn('min-h-[48px] w-full bg-transparent py-md', 'text-heading-sm-bold text-default', 'placeholder:text-heading-sm-bold placeholder:text-[#CACBD8]', 'focus:outline-none')} onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleSubmit();
          }
        }} />
            </div>
            {errorMessage ? <p className="text-caption-md-medium text-red-500">
                {errorMessage}
              </p> : null}
          </div>
        </div>} footer={<Button variant="contained" intent="strong" size="lg" className="w-full" disabled={!isValid || isPending} onClick={handleSubmit}>
          {submitLabel}
        </Button>} data-sentry-element="DefaultDialog" data-sentry-component="ClassroomNameFormDialog" data-sentry-source-file="ClassroomCreateModals.tsx" />;
}
export function CreateClassroomModal({
  onClose,
  onCreated,
  onSubmitTrack,
  onSuccessTrack,
  onErrorTrack
}: {
  onClose: () => void;
  onCreated: (data: CreatedClassroom) => void;
  onSubmitTrack?: (name: string) => void;
  onSuccessTrack?: (data: CreatedClassroom) => void;
  onErrorTrack?: (error: unknown) => void;
}) {
  const {
    t
  } = useTranslation(['classroom']);
  const createMutation = useCreateClassroom();
  const submitLockRef = useRef(false);
  const [isSubmitLocked, setIsSubmitLocked] = useState(false);
  const unlockSubmit = () => {
    submitLockRef.current = false;
    setIsSubmitLocked(false);
  };
  const handleSubmit = (name: string) => {
    if (submitLockRef.current || createMutation.isPending) return;
    onSubmitTrack?.(name);
    submitLockRef.current = true;
    setIsSubmitLocked(true);
    createMutation.mutate({
      name
    }, {
      onSuccess: data => {
        const created = {
          classroomId: data.classGroupId,
          name: data.name,
          ownerName: data.ownerName,
          ownerAvatarData: data.ownerAvatarData,
          memberCount: data.memberCount
        };
        onSuccessTrack?.(created);
        onCreated(created);
      },
      onError: error => {
        unlockSubmit();
        onErrorTrack?.(error);
        confirmDialog.open({
          header: {
            title: t('classroom:createClassroomModal.errorTitle')
          },
          body: t('classroom:createClassroomModal.createError'),
          confirmText: t('classroom:createClassroomModal.errorConfirm')
        });
      }
    });
  };
  return <ClassroomNameFormDialog title={t('classroom:createClassroomModal.title')} labelText={t('classroom:createClassroomModal.nameLabel')} submitLabel={t('classroom:createClassroomModal.submitLabel')} placeholder={t('classroom:createClassroomModal.namePlaceholder')} onSubmit={handleSubmit} onClose={onClose} isPending={createMutation.isPending || isSubmitLocked} data-sentry-element="ClassroomNameFormDialog" data-sentry-component="CreateClassroomModal" data-sentry-source-file="ClassroomCreateModals.tsx" />;
}
export function EditClassroomNameModal({
  classroomId,
  name: initialName,
  onClose,
  onSubmitTrack,
  onSuccessTrack,
  onErrorTrack
}: {
  classroomId: string;
  name: string;
  onClose: () => void;
  onSubmitTrack?: (name: string) => void;
  onSuccessTrack?: () => void;
  onErrorTrack?: (error: unknown) => void;
}) {
  const {
    t
  } = useTranslation(['classroom']);
  const [errorMessage, setErrorMessage] = useState<string>();
  const updateMutation = useUpdateClassroom();
  const handleSubmit = (name: string) => {
    onSubmitTrack?.(name);
    updateMutation.mutate(classroomId, {
      name
    }, {
      onSuccess: () => {
        onSuccessTrack?.();
        onClose();
      },
      onError: error => {
        onErrorTrack?.(error);
        setErrorMessage(t('classroom:createClassroomModal.editNameError'));
      }
    });
  };
  return <ClassroomNameFormDialog title={t('classroom:createClassroomModal.editTitle')} labelText={t('classroom:createClassroomModal.editNameLabel')} initialName={initialName} submitLabel={t('classroom:createClassroomModal.editSubmitLabel')} onSubmit={handleSubmit} onClose={onClose} isPending={updateMutation.isPending} errorMessage={errorMessage} onErrorMessageClear={() => setErrorMessage(undefined)} data-sentry-element="ClassroomNameFormDialog" data-sentry-component="EditClassroomNameModal" data-sentry-source-file="ClassroomCreateModals.tsx" />;
}